import { useForm } from '@mantine/form';
import { Select, Box, SimpleGrid, Text, Stack } from '@mantine/core';
import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import {
	useGetAllLocalizationTimezonesQuery,
	OrganizationIndustryEnum,
	CurrencyEnum,
	MeasurementSystemEnum,
} from '@codezo/graphql';

interface OrganizationSettingsStepProps {
	data: Record<string, unknown>;
	onDataChange: (_data: Record<string, unknown>) => void;
	onValidSubmit: (_data: Record<string, unknown>) => void;
	onPrevious: () => void;
	isFirstStep: boolean;
	isLastStep: boolean;
	triggerValidation: number;
}

const industryOptions = Object.values(OrganizationIndustryEnum).map((industry) => ({
	value: industry,
	label: industry.charAt(0).toUpperCase() + industry.slice(1).toLowerCase().replace(/_/g, ' '),
}));

const currencyOptions = Object.values(CurrencyEnum).map((currency) => ({
	value: currency,
	label: currency,
}));

const measurementSystemOptions = Object.values(MeasurementSystemEnum).map((system) => ({
	value: system,
	label: system,
}));

export function OrganizationSettingsStep({
	data,
	onDataChange,
	onValidSubmit,
	triggerValidation,
}: OrganizationSettingsStepProps) {
	const { data: timezonesData } = useGetAllLocalizationTimezonesQuery();
	const lastTriggerRef = useRef(0);

	const timezoneOptions =
		timezonesData?.getAllLocalizationTimezones?.map((timezone) => ({
			value: timezone.name,
			label: `${timezone.name} (${timezone.offset})`,
		})) || [];

	// Simply use data from parent - the first step already set the defaults!
	const form = useForm({
		initialValues: {
			industry: (data.industry as string) || '',
			currency: (data.currency as string) || '',
			measurementSystem: (data.measurementSystem as string) || '',
			timezone: (data.timezone as string) || '',
		},
		validate: {
			currency: (value) => (value ? null : 'Currency is required'),
			measurementSystem: (value) => (value ? null : 'Measurement system is required'),
			timezone: (value) => (value ? null : 'Timezone is required'),
		},
	});

	const handleSubmit = (values: typeof form.values) => {
		onValidSubmit(values);
	};

	// Update parent component whenever form values change (without validation)
	// Skip the first render to avoid overwriting parent data with empty values
	const isFirstRender = useRef(true);
	const previousValues = useRef(form.values);

	useEffect(() => {
		// Only update if values actually changed
		if (JSON.stringify(previousValues.current) === JSON.stringify(form.values)) {
			return;
		}
		if (isFirstRender.current) {
			isFirstRender.current = false;
			// Removed console.log
			previousValues.current = form.values;
			return;
		}
		// Removed console.log
		onDataChange(form.values);
		previousValues.current = form.values;
	}, [form.values, onDataChange]);

	// Handle validation trigger from parent
	useEffect(() => {
		if (triggerValidation > 0 && triggerValidation !== lastTriggerRef.current) {
			lastTriggerRef.current = triggerValidation;

			const validationResult = form.validate();

			if (!validationResult.hasErrors) {
				onValidSubmit(form.values);
			}
		}
	}, [triggerValidation, form, onValidSubmit]);

	return (
		<Box>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="xl">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 0.1 }}
					>
						<Box>
							<Text size="md" fw={500} mb="md">
								Organization Settings
							</Text>
							{data.countryCode ? (
								<Text size="sm" c="dimmed" mb="md">
									Settings have been pre-filled based on your location. You can
									adjust them if needed.
								</Text>
							) : null}
							<SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
								<motion.div
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.3, delay: 0.2 }}
								>
									<Select
										label="Industry"
										placeholder="Select your industry"
										data={industryOptions}
										searchable
										clearable
										value={form.values.industry}
										error={form.errors.industry}
										onChange={(value) =>
											form.setFieldValue('industry', value || '')
										}
									/>
								</motion.div>

								<motion.div
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.3, delay: 0.3 }}
								>
									<Select
										label="Currency"
										placeholder="Select your currency"
										data={currencyOptions}
										searchable
										required
										value={form.values.currency}
										error={form.errors.currency}
										onChange={(value) => {
											// Don&apos;t allow clearing required field
											if (value) {
												form.setFieldValue('currency', value);
											}
										}}
									/>
								</motion.div>

								<motion.div
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.3, delay: 0.4 }}
								>
									<Select
										label="Measurement System"
										placeholder="Select your measurement system"
										data={measurementSystemOptions}
										required
										value={form.values.measurementSystem}
										error={form.errors.measurementSystem}
										onChange={(value) => {
											// Don&apos;t allow clearing required field
											if (value) {
												form.setFieldValue('measurementSystem', value);
											}
										}}
									/>
								</motion.div>

								<motion.div
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.3, delay: 0.5 }}
								>
									<Select
										label="Timezone"
										placeholder="Select your timezone"
										data={timezoneOptions}
										searchable
										required
										value={form.values.timezone}
										error={form.errors.timezone}
										onChange={(value) => {
											// Don&apos;t allow clearing required field
											if (value) {
												form.setFieldValue('timezone', value);
											}
										}}
									/>
								</motion.div>
							</SimpleGrid>
						</Box>
					</motion.div>
				</Stack>
			</form>
		</Box>
	);
}
